import {
    useMutation, useQuery, useQueryClient, UseQueryOptions, 
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
    collection, getDoc, getDocs, doc, setDoc, updateDoc, deleteDoc,
} from 'firebase/firestore';

import { db } from '@/firestore/config.ts';
import { FbFilters } from '@/firestore/firestore.types.ts';
import { prepareFbFilters } from '@/firestore/utils/prepareFbFilters.ts';
import { stringifyFbFilters } from '@/firestore/utils/stringifyFbFilters.ts';

import http from './http'
import { UseQueryCustomParams } from '../api.types.ts';

/**
 * No message for fetch because there is a risk to spam user with error messages, and we have screens for crashes
 * Other error messages are optional if required in specific endpoint, in other cases no message would be shown
 */
interface GetRequestQueriesParams {
    url: string
    errorMessageFetchCSV?: string
    errorMessageUpdate?: string
    errorMessageCreate?: string
    requiredFilters?: Array<keyof ResultItem> // Add this field
}

interface FetchItemsParams<T> {
    queryParams?: T
    filterString?: string
    pageLimit?: number
    pageOffset?: number
    id?: number | string
}

const defaultItemsQueryOptions = {
    initialData: [],
    /**
             * Don't retry by default
             */
    retry: false,
    /**
             * If 0 every new component mout it will be refetched
             * Different from cacheTime
             */
    staleTime: 12000,
    /**
             * Ignores staleTime for the first fetch
             */
    initialDataUpdatedAt: 0,
}

/**
 * @todo: Make Firebase listeners work and invalidate queries
 *
 * @todo: Prop command for generating api requests
 * @todo: Validate closing slash '/' in the end of url
 * @todo: Turn off autoupdate by default form make migration more easy
 * @todo: Add flag "isModel" or something like this. For endpoints which is build on database table we have more default functionality.
 * @todo: Implement linking with other fetched data?
 * @todo: How to load items in background without triggering loaders?
 * @todo: Cache works not as expected sometimes and hooks doing requests even if data is cached
 * @todo: Stop doing autoupdate if user logged out or token expired if property "refetchInterval" is used
 * @todo: Sorting props (for tables)
 * @todo: Create hook
 * @todo: Migrate 'filrtersString' to object (it can me new method fro filters 'toObject')
 * @todo: Pause react-query refetch in background when tab is not active
 */
export const getRequestQueries = <ResultItem, GetItemsQueryParams>(params: GetRequestQueriesParams) => {
    const {
        url, errorMessageUpdate, errorMessageCreate, requiredFilters = [],
    } = params

    const commonHeaders = { 'react-query': 'true' }

    async function fetchItems ({
        queryParams, filterString, pageLimit, pageOffset, id, 
    }: FetchItemsParams<GetItemsQueryParams>) {
        const paginationParams: Record<string, number> = { }

        if (pageOffset >= 0 && pageLimit >= 0) {
            paginationParams.__limit = pageLimit
            paginationParams.__offset = pageOffset
        }

        let requestUrl = `${url}/` + (id ? `${id}/` : '')
        requestUrl = filterString ? `${requestUrl}?${filterString}` : requestUrl

        const response = await http.get<ResultItem[]>(requestUrl, {
            params: {
                ...queryParams,
                ...paginationParams,
            },
            headers: commonHeaders,
        })

        // Wrapping in 'Array' for make typings compatible for case with 'id' and without
        return id ? [response.data] as unknown as ResultItem[] : response.data
    }

    async function fetchFBItems(params: UseFBQueryCustomParams) {
        const {
            id, filters, includeDeleted = false, 
        } = params

        // Alert about missed filers
        if (requiredFilters.length > 0 && !id) {
            const missingFilters = requiredFilters.filter(filterKey => {
                if (!filters) return true;
                
                // Check if we have a single condition
                if (Array.isArray(filters) && filters.length === 3 && typeof filters[0] === 'string') {
                    // Single condition case: [field, operator, value]
                    return filters[0] !== filterKey;
                }
                
                // Group condition case: ['and'/'or', ...conditions]
                if (Array.isArray(filters) && (filters[0] === 'and' || filters[0] === 'or')) {
                    const [_, ...conditions] = filters;
                    // Check if any condition matches the required filter
                    return !conditions.some(condition => {
                        // Only check first-level conditions
                        if (Array.isArray(condition) && condition.length === 3 && typeof condition[0] === 'string') {
                            return condition[0] === filterKey && condition[2] !== undefined;
                        }
                        return false;
                    });
                }
                
                return true; // If we can't determine, mark as missing
            });
            
            if (missingFilters.length > 0) {
                // ENV: Create error with stack trace to show meaningful caller info
                const errorWithStack = new Error();
                const stackLines = errorWithStack.stack?.split('\n') || [];
                
                // Filter stack to only include project files, not libraries
                const projectFilePattern = /http:\/\/localhost:\d+\/src\/(?!node_modules)/;
                const relevantStack = stackLines
                    .filter(line => projectFilePattern.test(line))
                    .slice(1, 6) // Skip this function and take up to 5 project files
                    .join('\n');
                
                console.error(
                    `Missing required filters for ${url}: ${missingFilters.join(', ')}\n` +
                    `Called from:\n${relevantStack || 'Could not determine source'}`,
                );
            }
        }
        
        const collectionRef = collection(db, url)

        // Firebase logic
        if (id) {
            const docRef = doc(collectionRef, id)
            const docSnap = await getDoc(docRef)
            return [{
                id: docSnap.id,
                ...docSnap.data(), 
            }]
        }

        const requestQuery = filters && prepareFbFilters(collectionRef, filters)

        const querySnapshot = await getDocs(
            requestQuery ? requestQuery : collectionRef,
        )

        let errorShown = false

        const items = querySnapshot.docs.map(doc => {
            const data = doc.data()
            if ('id' in data && !errorShown) {
                console.error(
                    `Document in collection "${url}" with ID "${doc.id}" contains 'id' field in its data. ` +
                    'This might cause conflicts as the document ID will overwrite this field.',
                )
                errorShown = true
            }
            return {
                id: doc.id,
                ...data, 
            }
        })
            .filter(el => includeDeleted || (!includeDeleted && !el.deletedAt))

        return items
    }

    async function fetchItemById ({ id }) {
        const requestUrl = `${url}/${id}/`

        const response = await http.get<ResultItem>(requestUrl, { headers: commonHeaders })

        return response.data
    }

    async function updateItemById ({
        id, data, 
    }) {
        const requestUrl = `${url}/${id}/`

        const response = await http.patch<ResultItem>(
            requestUrl,
            data,
            {
                meta: { errorToast: errorMessageUpdate },
                headers: commonHeaders,
            },
        )

        return response.data
    }

    function useItemsQuery (
        customParams?: UseQueryCustomParams<GetItemsQueryParams>,
        queryOptions?: Omit<UseQueryOptions<Promise<ResultItem[]>, AxiosError, ResultItem[]>, 'queryKey'>,
    ) {
        const {
            pageKey, queryParams, filter, pageLimit, pageOffset, id, 
        } = customParams ?? {}

        const queryOptionsUpdated: UseQueryOptions<Promise<ResultItem[]>, AxiosError, ResultItem[]> = {
            ...defaultItemsQueryOptions,
            ...(queryOptions ?? {}),
        }

        const filterString = filter?.URLSearchParams.toString() ?? ''

        return useQuery <Promise<ResultItem[]>, AxiosError, ResultItem[]>({
            queryKey: [url, pageKey, queryParams, filterString, pageOffset, pageLimit],
            queryFn: () => fetchItems({
                queryParams,
                filterString,
                pageOffset,
                pageLimit,
                id, 
            }),
            ...queryOptionsUpdated,
        })
    }

    // Add new interface for FB custom params
    interface UseFBQueryCustomParams {
        /**
         * @todo: make 'and' not required for the first level of nesting (it'll be default)
         */
        filters?: FbFilters<ResultItem>
        id?: string | number | null
        includeDeleted?: boolean 
    }

    function useFBItemsQuery (
        customParams?: UseFBQueryCustomParams,
        queryOptions?: Omit<UseQueryOptions<Promise<ResultItem[]>, AxiosError, ResultItem[]>, 'queryKey'>,
    ) {
        const params = customParams || {}
        
        const queryOptionsUpdated: UseQueryOptions<Promise<ResultItem[]>, AxiosError, ResultItem[]> = {
            // @ts-expect-error Can't figure out how to solve types conflict in this case
            ...defaultItemsQueryOptions,
            ...(queryOptions ?? {}),
        }
        
        const filtersString = stringifyFbFilters(params.filters)

        return useQuery<Promise<ResultItem[]>, AxiosError, ResultItem[]>({
            queryKey: [url, filtersString, params.id],
            queryFn: () => fetchFBItems({ ...params }),
            ...queryOptionsUpdated,
        })
    }

    function useItemByIdQuery (
        id: string | number,
        queryOptions?: Omit<UseQueryOptions<Promise<ResultItem>, AxiosError, ResultItem>, 'queryKey'>,
    ) {
        const queryOptionsUpdated: Omit<UseQueryOptions<Promise<ResultItem>, AxiosError, ResultItem>, 'queryKey' > = { ...(queryOptions ?? {}) }

        return useQuery <Promise<ResultItem>, AxiosError, ResultItem>({
            queryKey: [url, id],
            queryFn: () => fetchItemById({ id }),
            ...queryOptionsUpdated,
        })
    }

    async function fetchFBItemById({ id }: { id?: string | null }) {
        if(!id) return undefined

        const docRef = doc(db, url, id)
        const docSnap = await getDoc(docRef)
        
        if (!docSnap.exists()) {
            throw new Error(`Document with id ${id} not found in collection ${url}`)
        }

        // ENV: Check if document contains 'id' field to prevent conflicts
        if ('id' in docSnap.data()) {
            console.error(
                `Document in collection "${url}" with ID "${id}" contains 'id' field in its data. ` +
                'This might cause conflicts as the document ID will overwrite this field.',
            )
        }

        return {
            id: docSnap.id,
            ...docSnap.data(), 
        } as ResultItem
    }

    function useFBItemByIdQuery(
        // Undefined for async loading of id
        id?: string | null,
        queryOptions?: Omit<UseQueryOptions<Promise<ResultItem>, AxiosError, ResultItem>, 'queryKey'>,
    ) {
        const queryOptionsUpdated: Omit<UseQueryOptions<Promise<ResultItem>, AxiosError, ResultItem>, 'queryKey'> = { ...(queryOptions ?? {}) }

        return useQuery<Promise<ResultItem>, AxiosError, ResultItem>({
            queryKey: [url, id],
            queryFn: () => fetchFBItemById({ id }),
            ...queryOptionsUpdated,
        })
    }

    const useItemUpdateMutation = () => {
        const queryClient = useQueryClient()

        return useMutation({
            mutationFn: updateItemById,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [url] })
            },
        })
    }

    const createItem = async (data: ResultItem) => {
        const response = await http.post<ResultItem>(
            url + '/',
            data,
            {
                meta: { errorToast: errorMessageCreate },
                headers: commonHeaders,
            },
        )

        return response.data
    }

    const useItemCreateMutation = () => {
        const queryClient = useQueryClient()

        return useMutation({
            mutationFn: createItem,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [url] })
            },
        })
    }

    // Add new Firebase mutation functions
    async function createFBItem({
        data,
        customId,
    }: {
        data: Omit<ResultItem, 'id'>,
        customId?: string
    }) {
        // ENV: Generate new document with auto-generated ID or use custom ID
        const collectionRef = collection(db, url)
        const docRef = customId ? doc(collectionRef, customId) : doc(collectionRef)
        
        // ENV: Check if data contains 'id' field to prevent conflicts
        if ('id' in data) {
            console.error(
                'Attempting to create a document with an \'id\' field in its data. ' +
                'This might cause conflicts as the document ID will overwrite this field.',
            )
        }
        
        await setDoc(docRef, data)
        return {
            id: docRef.id,
            ...data, 
        } as ResultItem
    }

    async function updateFBItemById({
        id, data, 
    }: { id: string, data: Partial<ResultItem> }) {
        if (!id) throw new Error('ID is required for updating a document')
        
        // ENV: Check if update data contains 'id' field to prevent conflicts
        if ('id' in data) {
            console.error(
                'Attempting to update a document with an \'id\' field in the update data. ' +
                'This might cause conflicts as the document ID will overwrite this field.',
            )
        }
        
        const docRef = doc(db, url, id)
        await updateDoc(docRef, data)
        
        // Return updated document
        const updatedDoc = await getDoc(docRef)
        return {
            id: updatedDoc.id,
            ...updatedDoc.data(), 
        } as ResultItem
    }

    const useFBItemCreateMutation = () => {
        const queryClient = useQueryClient()

        return useMutation({
            mutationFn: createFBItem,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [url] })
            },
        })
    }

    const useFBItemUpdateMutation = () => {
        const queryClient = useQueryClient()

        return useMutation({
            mutationFn: updateFBItemById,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [url] })
            },
        })
    }

    // Add new Firebase delete function
    async function deleteFBItemById({ id }: { id: string }) {
        if (!id) throw new Error('ID is required for deleting a document')
        
        const docRef = doc(db, url, id)
        await deleteDoc(docRef)
        
        return { id }
    }

    const useFBItemDeleteMutation = () => {
        const queryClient = useQueryClient()

        return useMutation({
            mutationFn: deleteFBItemById,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [url] })
            },
        })
    }

    async function fetchFBItemsOnce(
        customParams?: UseFBQueryCustomParams,
    ): Promise<ResultItem[]> {
        const params = customParams || {}

        return fetchFBItems({ ...params })
    }

    return {
        useItemsQuery,
        useFBItemsQuery,
        useItemByIdQuery,
        useFBItemByIdQuery,
        useItemUpdateMutation,
        useItemCreateMutation,
        useFBItemCreateMutation,
        useFBItemUpdateMutation,
        useFBItemDeleteMutation,
        fetchFBItemsOnce,
    }
}
